/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import { font, paragraphXSmall, weightSemiBold } from 'src/components/css'

export interface ChipGroupOption {
  value: string
  label: string
  /**
   * Hover / keyboard-focus hint (antd `Tooltip`); useful when the label is
   * abbreviated. No tooltip when absent.
   */
  tooltip?: string
  /**
   * Leading glyph. Leave its colour unset: it inherits the chip's text colour
   * through `currentColor`, so it flips with the selected state.
   */
  icon?: ReactNode
  /** Not clickable and dimmed; pair with `tooltip` to say why. */
  disabled?: boolean
}

/** A selected chip's fill (also its border) and its text / icon colour. */
export interface ChipSelectedColor {
  fill: string
  content: string
}

export interface ChipGroupProps {
  options: ChipGroupOption[]
  /** Single-select: the selected option value, or `null` when none. */
  value?: string | null
  /**
   * Multi-select: every selected option value. When set it wins over `value`;
   * `onChange` then receives the clicked value to toggle.
   */
  values?: string[]
  onChange: (value: string) => void
  /** Names the group for assistive tech. Already translated. */
  ariaLabel: string
  /**
   * Colours of a selected chip, per option value. Defaults to the tenant
   * primary with `PRIMARY_BUTTON_CONTENT.text`.
   */
  selectedColor?: (value: string) => ChipSelectedColor
}

const palette = colors as Record<string, string>

const DEFAULT_SELECTED_COLOR: ChipSelectedColor = {
  fill: colors.PRIMARY,
  content: PRIMARY_BUTTON_CONTENT.text,
}

/** Single- or multi-select chip row (sport chips, stat chips). */
const ChipGroup = ({
  options,
  value = null,
  values,
  onChange,
  ariaLabel,
  selectedColor,
}: ChipGroupProps) => {
  'use memo'

  return (
    <div role='group' aria-label={ariaLabel} css={containerStyles}>
      {options.map((option) => {
        const isSelected = values
          ? values.includes(option.value)
          : option.value === value
        const chipColor =
          isSelected && selectedColor
            ? selectedColor(option.value)
            : DEFAULT_SELECTED_COLOR
        const button = (
          <button
            key={option.value}
            type='button'
            aria-pressed={isSelected}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            css={chipStyles(isSelected, chipColor)}
          >
            {option.icon}
            {capitalizeWords(option.label)}
          </button>
        )
        // A disabled button fires no mouse events, so a wrapper takes the
        // tooltip hover and the not-allowed cursor.
        const chip = option.disabled ? (
          <span key={option.value} css={disabledWrapStyles}>
            {button}
          </span>
        ) : (
          button
        )
        return option.tooltip ? (
          <Tooltip
            key={option.value}
            title={option.tooltip}
            trigger={['hover', 'focus']}
          >
            {chip}
          </Tooltip>
        ) : (
          chip
        )
      })}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const chipStyles = (isSelected: boolean, selected: ChipSelectedColor) => css`
  ${font.body}
  ${paragraphXSmall}
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.2s ease;
  background: ${isSelected ? selected.fill : palette.SECONDARY_350};
  border: 1px solid ${isSelected ? selected.fill : colors.SECONDARY_200};
  color: ${isSelected ? selected.content : colors.WHITE};

  &:hover {
    border-color: ${isSelected ? selected.fill : colors.SECONDARY_100};
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`

const disabledWrapStyles = css`
  display: inline-flex;
  cursor: not-allowed;
`

export default ChipGroup
